<template>
  <b-modal
    :visible="!!id"
    size="lg"
    centered
    scrollable
    hide-footer
    :title="
      sheet ? `${sheet.tradie.name} · ${sheet.project.name}` : 'Timesheet'
    "
    data-test="external-detail"
    @hidden="$emit('close')"
  >
    <p v-if="loading" class="text-muted mb-0" data-test="detail-loading">
      Loading…
    </p>
    <b-alert
      v-else-if="loadError"
      show
      variant="danger"
      data-test="detail-error"
    >
      {{ loadError }}
    </b-alert>

    <div v-else-if="sheet" class="detail">
      <div
        class="d-flex flex-wrap justify-content-between align-items-start mb-3"
      >
        <div>
          <div class="text-muted">Week of {{ week }}</div>
          <div v-if="sheet.supervisor" class="text-muted">
            Supervisor: {{ sheet.supervisor.name
            }}<span v-if="sheet.supervisor.company">
              ({{ sheet.supervisor.company }})</span
            >, {{ sheet.supervisor.email }}
          </div>
        </div>
        <div class="text-right">
          <span
            class="chip"
            :class="`chip--${meta.tone}`"
            data-test="detail-status"
          >
            {{ meta.label }}
          </span>
          <span v-if="sheet.overdue" class="chip chip--bad ml-1">Overdue</span>
        </div>
      </div>

      <b-alert
        v-if="sheet.admin_approval"
        show
        variant="info"
        data-test="admin-approval"
      >
        Approved by company admin
        <strong>{{ sheet.admin_approval.by }}</strong
        >. Reason: {{ sheet.admin_approval.reason }}
      </b-alert>
      <b-alert
        v-if="
          request &&
          request.comment &&
          sheet.status !== 'awaiting_external_approval'
        "
        show
        variant="light"
        class="border"
        data-test="supervisor-comment"
      >
        Supervisor's comment: {{ request.comment }}
      </b-alert>

      <h6 class="font-weight-bold">Hours</h6>
      <div class="table-scroll">
        <table class="table table-sm" data-test="days">
          <thead>
            <tr>
              <th>Day</th>
              <th>Start</th>
              <th>Finish</th>
              <th>Break</th>
              <th class="text-right">Worked</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="d in days"
              :key="d.date"
              :class="{ 'row-changed': d.was }"
              :data-test="`day-${d.date}`"
            >
              <td>{{ dayLabel(d.date) }}</td>
              <template v-if="d.leave_type">
                <td colspan="3" class="text-muted">
                  {{ leaveLabel(d.leave_type) }}
                </td>
                <td class="text-right">–</td>
              </template>
              <template v-else-if="!editing">
                <td>{{ hm(d.start) || '–' }}</td>
                <td>{{ hm(d.finish) || '–' }}</td>
                <td>{{ d.start ? `${d.break_minutes || 0}m` : '–' }}</td>
                <td class="text-right">
                  {{ d.start ? format(worked(d)) : '–' }}
                  <div v-if="d.was" class="was" :data-test="`was-${d.date}`">
                    Was {{ d.was }}
                  </div>
                </td>
              </template>
              <template v-else>
                <td>
                  <b-form-input
                    v-model="draft[d.date].start"
                    type="time"
                    size="sm"
                    :data-test="`edit-start-${d.date}`"
                  />
                </td>
                <td>
                  <b-form-input
                    v-model="draft[d.date].finish"
                    type="time"
                    size="sm"
                    :data-test="`edit-finish-${d.date}`"
                  />
                </td>
                <td>
                  <b-form-input
                    v-model.number="draft[d.date].break_minutes"
                    type="number"
                    min="0"
                    size="sm"
                    :data-test="`edit-break-${d.date}`"
                  />
                </td>
                <td class="text-right">
                  {{ format(draftMinutes(d.date)) }}
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th colspan="4">Total</th>
              <th class="text-right" data-test="total">
                {{ format(editing ? draftTotal : sheet.total_minutes) }}
              </th>
            </tr>
          </tfoot>
        </table>
      </div>

      <p v-if="sheet.comment" class="mb-3" data-test="tradie-comment">
        <strong>Tradie's note:</strong> {{ sheet.comment }}
      </p>

      <div v-if="sheet.photos && sheet.photos.length" class="mb-3">
        <h6 class="font-weight-bold">Photos</h6>
        <div class="photos" data-test="photos">
          <a
            v-for="photo in sheet.photos"
            :key="photo.id"
            :href="photo.url"
            target="_blank"
            rel="noopener"
          >
            <img :src="photo.url" alt="Timesheet photo" />
          </a>
        </div>
      </div>

      <b-alert
        v-if="actionError"
        show
        variant="danger"
        data-test="action-error"
      >
        {{ actionError }}
      </b-alert>

      <!-- Actions: only what makes sense for the sheet's state. -->
      <div v-if="editing" class="action-box" data-test="amend-form">
        <label for="amend-reason" class="font-weight-bold">
          Why are you changing these hours? (required)
        </label>
        <b-form-input
          id="amend-reason"
          v-model="reason"
          placeholder="e.g. Tradie confirmed he left at 2pm"
          data-test="amend-reason"
        />
        <p class="text-muted small mt-2 mb-2">
          Your changes are saved as a new version and a fresh link goes to the
          supervisor, so the sheet is awaiting their approval again.
        </p>
        <b-button
          variant="primary"
          :disabled="busy || !canSaveAmend"
          data-test="amend-save"
          @click="saveAmend"
        >
          Save and send to supervisor
        </b-button>
        <b-button variant="link" :disabled="busy" @click="editing = false"
          >Cancel</b-button
        >
      </div>

      <div v-else-if="behalf" class="action-box" data-test="behalf-form">
        <label for="behalf-reason" class="font-weight-bold">
          Reason for approving on their behalf (required)
        </label>
        <b-form-input
          id="behalf-reason"
          v-model="reason"
          placeholder="e.g. Supervisor confirmed by phone"
          data-test="behalf-reason"
        />
        <p class="text-muted small mt-2 mb-2">
          This shows as "Approved by company admin", never as the supervisor's
          approval. You still finalise it separately.
        </p>
        <b-button
          variant="primary"
          :disabled="busy || reason.trim().length < 5"
          data-test="behalf-save"
          @click="approveOnBehalf"
        >
          Approve on behalf
        </b-button>
        <b-button variant="link" :disabled="busy" @click="behalf = false"
          >Cancel</b-button
        >
      </div>

      <div v-else class="d-flex flex-wrap" style="gap: 8px">
        <b-button
          v-if="finalisable"
          variant="primary"
          :disabled="busy"
          data-test="finalise"
          @click="finalise"
        >
          Finalise
        </b-button>
        <b-button
          v-if="awaiting"
          variant="outline-primary"
          :disabled="busy"
          data-test="behalf-start"
          @click="startBehalf"
        >
          Approve on behalf
        </b-button>
        <b-button
          v-if="awaiting"
          variant="outline-secondary"
          :disabled="busy"
          data-test="resend"
          @click="resend"
        >
          Send a new link
        </b-button>
        <b-button
          v-if="awaiting || finalisable"
          variant="outline-secondary"
          :disabled="busy"
          data-test="amend-start"
          @click="startAmend"
        >
          Change hours
        </b-button>
        <b-button
          v-if="sheet.status === 'finalised'"
          variant="outline-danger"
          :disabled="busy"
          data-test="unfinalise"
          @click="unfinalise"
        >
          Unfinalise
        </b-button>
        <span
          v-if="sheet.status === 'rejected'"
          class="text-muted align-self-center"
        >
          The tradie amends and sends it again.
        </span>
      </div>

      <h6 class="font-weight-bold mt-4">History</h6>
      <ul class="list-unstyled small mb-0" data-test="audit">
        <li v-for="(a, i) in audit" :key="i" class="mb-1">
          <span class="text-muted">{{ when(a.performed_at) }}</span>
          · {{ a.performed_by }}: {{ auditText(a) }}
        </li>
        <li v-if="!audit.length" class="text-muted">Nothing recorded yet.</li>
      </ul>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import apiToasts from '@/mixins/apiToasts'
import {
  formatMinutes,
  hm,
  isFinalisable,
  statusMeta,
  weekLabel,
  firstError,
} from '@/utils/externalTimesheets'

const LEAVE = {
  public_holiday: 'Public holiday',
  personal_leave: 'Personal leave',
  sick_leave: 'Sick leave',
  annual_leave: 'Annual leave',
}

// What each audit action reads as. Unknown ones show their note or the action.
const AUDIT = {
  draft_saved: 'Draft saved',
  submitted: 'Sent for approval',
  resubmitted: 'Sent for approval again',
  withdrawn: 'Withdrawn by the tradie',
  link_resent: 'New link emailed',
  ext_link_sent: 'Approval link emailed',
  ext_link_resent: 'New link emailed',
  ext_code_sent: 'Code emailed to the supervisor',
  ext_code_verified: 'Supervisor entered the code',
  ext_code_failed: 'Wrong code entered',
  ext_code_locked: 'Code locked after too many wrong tries',
  ext_opened: 'Supervisor opened the timesheet',
  ext_reminder_sent: 'Reminder emailed',
  ext_approved: 'Approved',
  ext_amended: 'Approved with changes',
  ext_rejected: 'Rejected',
  ext_finalised: 'Finalised',
  ext_unfinalised: 'Unfinalised',
  ext_admin_approved: 'Approved by company admin',
  ext_admin_amended: 'Hours changed by company admin',
  ext_overdue: 'No answer after a week',
  ext_photos_downloaded: 'Photos downloaded',
}

const toMinutes = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(t || '')
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

export default {
  name: 'ExternalTimesheetDetailModal',

  mixins: [apiToasts],

  props: {
    id: { type: [Number, String], default: null },
  },

  data() {
    return {
      sheet: null,
      loading: false,
      loadError: '',
      busy: false,
      actionError: '',
      editing: false,
      behalf: false,
      reason: '',
      draft: {},
    }
  },

  computed: {
    meta() {
      return statusMeta(this.sheet && this.sheet.status)
    },
    week() {
      return this.sheet
        ? weekLabel(this.sheet.week_start, this.sheet.week_end)
        : ''
    },
    request() {
      return this.sheet && this.sheet.external_request
    },
    awaiting() {
      return this.sheet && this.sheet.status === 'awaiting_external_approval'
    },
    finalisable() {
      return this.sheet && isFinalisable(this.sheet.status)
    },
    audit() {
      return (this.sheet && this.sheet.audit) || []
    },
    // The newest version that changed hours: "Was 07:00–15:30" on those days.
    wasByDate() {
      const out = {}
      const versions = (this.sheet && this.sheet.versions) || []
      versions.forEach((v) => {
        ;(v.amended_days || []).forEach((a) => {
          out[a.date] = a.original_start
            ? `${a.original_start}–${a.original_finish}`
            : 'no hours'
        })
      })
      return out
    },
    days() {
      return ((this.sheet && this.sheet.days) || []).map((d) => ({
        ...d,
        was: this.wasByDate[d.date] || '',
      }))
    },
    draftTotal() {
      return Object.keys(this.draft).reduce(
        (sum, date) => sum + this.draftMinutes(date),
        0,
      )
    },
    canSaveAmend() {
      return this.reason.trim().length >= 5 && this.changedDays().length > 0
    },
  },

  watch: {
    id: {
      immediate: true,
      handler(id) {
        this.reset()
        if (id) this.load()
      },
    },
  },

  methods: {
    ...mapActions('externalTimesheet', [
      'fetchExternalTimesheet',
      'finaliseExternalTimesheet',
      'unfinaliseExternalTimesheet',
      'approveExternalTimesheetOnBehalf',
      'resendExternalTimesheetLink',
      'amendExternalTimesheet',
    ]),

    hm,
    format: formatMinutes,

    reset() {
      this.sheet = null
      this.loadError = ''
      this.actionError = ''
      this.editing = false
      this.behalf = false
      this.reason = ''
      this.draft = {}
    },

    async load() {
      this.loading = true
      try {
        this.sheet = await this.fetchExternalTimesheet({ id: this.id })
      } catch (err) {
        this.loadError = this.errorMessage(
          err,
          'Could not load this timesheet.',
        )
      } finally {
        this.loading = false
      }
    },

    dayLabel(date) {
      const d = new Date(`${date}T00:00:00`)
      return d.toLocaleDateString('en-AU', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
    },
    leaveLabel: (type) => LEAVE[type] || type,
    when(iso) {
      return iso
        ? new Date(iso).toLocaleString('en-AU', {
            day: 'numeric',
            month: 'short',
            hour: 'numeric',
            minute: '2-digit',
          })
        : ''
    },
    // The action in plain words, plus the reason or comment someone typed.
    auditText(a) {
      const base = AUDIT[a.action] || a.action
      const typed = /(Reason|Comment): (.+)$/s.exec(a.note || '')
      return typed ? `${base}. ${typed[1]}: ${typed[2]}` : base
    },

    worked(d) {
      const start = toMinutes(d.start)
      const finish = toMinutes(d.finish)
      if (start === null || finish === null) return 0
      const span = finish > start ? finish - start : finish + 1440 - start
      return Math.max(0, span - (Number(d.break_minutes) || 0))
    },

    // ── amend ─────────────────────────────────────────────────────────────
    startAmend() {
      this.actionError = ''
      this.reason = ''
      this.draft = {}
      this.days
        .filter((d) => !d.leave_type)
        .forEach((d) => {
          this.$set(this.draft, d.date, {
            start: hm(d.start),
            finish: hm(d.finish),
            break_minutes: Number(d.break_minutes) || 0,
          })
        })
      this.editing = true
    },
    draftMinutes(date) {
      const d = this.draft[date]
      return d ? this.worked(d) : 0
    },
    changedDays() {
      return this.days
        .filter((d) => this.draft[d.date])
        .filter((d) => {
          const n = this.draft[d.date]
          return (
            (n.start || '') !== hm(d.start) ||
            (n.finish || '') !== hm(d.finish) ||
            (Number(n.break_minutes) || 0) !== (Number(d.break_minutes) || 0)
          )
        })
    },
    async saveAmend() {
      await this.act(
        () =>
          this.amendExternalTimesheet({
            id: this.id,
            reason: this.reason.trim(),
            days: this.changedDays().map((d) => ({
              date: d.date,
              start: this.draft[d.date].start || null,
              finish: this.draft[d.date].finish || null,
              break_minutes: Number(this.draft[d.date].break_minutes) || 0,
            })),
          }),
        'Hours changed. A new link was sent to the supervisor.',
      )
    },

    // ── the other actions ─────────────────────────────────────────────────
    startBehalf() {
      this.actionError = ''
      this.reason = ''
      this.behalf = true
    },
    approveOnBehalf() {
      return this.act(
        () =>
          this.approveExternalTimesheetOnBehalf({
            id: this.id,
            reason: this.reason.trim(),
          }),
        'Approved on behalf of the supervisor.',
      )
    },
    finalise() {
      return this.act(
        () => this.finaliseExternalTimesheet({ id: this.id }),
        'Finalised. The hours are with payroll.',
      )
    },
    unfinalise() {
      return this.act(
        () => this.unfinaliseExternalTimesheet({ id: this.id }),
        'Unfinalised. The hours were taken back from payroll.',
      )
    },
    resend() {
      return this.act(
        () => this.resendExternalTimesheetLink({ id: this.id }),
        'A new link was emailed to the supervisor.',
      )
    },

    // Runs one action, shows what the API said if it refused, and refreshes.
    async act(call, doneMessage) {
      if (this.busy) return
      this.busy = true
      this.actionError = ''
      try {
        this.sheet = await call()
        this.editing = false
        this.behalf = false
        this.reason = ''
        this.showToast('Done', doneMessage)
        this.$emit('changed')
      } catch (err) {
        this.actionError = firstError(err, 'That did not work. Try again.')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.photos {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
  img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 8px;
  }
}

.chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;

  &--wait {
    background: #fdf0d9;
    color: #9a5b00;
  }
  &--ok {
    background: #e3f4ea;
    color: #1d7a46;
  }
  &--bad {
    background: #fbe6e4;
    color: #b3261e;
  }
  &--done {
    background: #e6eefc;
    color: #1f5fe0;
  }
}

.table-scroll {
  overflow-x: auto;
}

.row-changed {
  background: #fff8e6;
}

.was {
  font-size: 12px;
  color: #6b7280;
}

.action-box {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
}
</style>
